import type { Character, CharacterOrigin, CrawlerType } from '../domain/character.ts';
import { CHARACTER_SCHEMA_VERSION } from '../domain/character.ts';
import type { StatBlock } from '../domain/stats.ts';
import { CRAWLER_HEALTH_BAR_SLOTS } from '../rules/health.ts';
import { maxMana } from '../rules/mana.ts';
import { startingAiFavor } from '../rules/aiFavor.ts';
import { HUMAN_CRAWLER_SIZE } from '../rules/size.ts';
import { emptyHotlist } from '../rules/hotlist.ts';

export interface NewCharacterInput {
  id: string;
  name: string;
  crawlerType: CrawlerType;
  stats: StatBlock;
  skills: { skillId: string; rank: number }[];
  /** Animal crawlers choose 2-4 (C&C p.16, Table 10). Humans are always 4. */
  size?: number;
  origin?: CharacterOrigin | null;
  now: string;
}

/**
 * Builds a brand new crawler record. Level 1, Floor 1: the Tutorial Floor.
 * Enhanced Stats are NOT stored; they are derived by rules/enhancedStats.ts.
 */
export function createCharacter(input: NewCharacterInput): Character {
  return {
    id: input.id,
    kind: 'character',
    schemaVersion: CHARACTER_SCHEMA_VERSION,
    name: input.name.trim(),
    crawlerType: input.crawlerType,
    level: 1,
    statAdvancement: { levelAccountedFor: 1, unspentPoints: 0, needsReview: false },
    skillRankLimits: {},
    floor: 1,
    unenhancedStats: { ...input.stats },
    skills: input.skills.map((s) => ({ ...s })),
    // Enhanced Stats equal Unenhanced at creation, so the Con Mod below is the
    // starting Con Mod. Health Bar slot value and Max Mana stay derived.
    healthBarSlotsRemaining: CRAWLER_HEALTH_BAR_SLOTS,
    currentMana: maxMana(input.stats.int),
    // Ten empty slots. A new crawler has nothing to put in them yet; the Heal
    // Spell it starts with is added by the player (C&C p.15).
    hotlist: emptyHotlist(),
    companions: [],
    aiFavor: startingAiFavor(input.crawlerType),
    size: input.crawlerType === 'human' ? HUMAN_CRAWLER_SIZE : (input.size ?? HUMAN_CRAWLER_SIZE),
    dyingRoundsRemaining: null,
    origin: input.origin ?? null,
    advancementMarks: [],
    inventory: [],
    changeLog: [],
    notes: '',
    createdAt: input.now,
    updatedAt: input.now,
  };
}
